import {
  runInAction,
  makeObservable,
  observable,
  action,
  computed,
  reaction,
  when,
  onBecomeObserved,
  onBecomeUnobserved,
} from "mobx";
import localforage from "localforage";
import type RootStore from "./RootStore";
import type { FeathersService } from "@feathersjs/feathers";
import { ReactionManager } from "./utils/ReactionManager";
import type {
  ResolveEntity,
  ResolveData,
  ResolvePatch,
  ResolveQuery,
  BaseEntity,
} from "../types/ServiceStoreTypeMap";

const SORT_DIRECTION_OPTIONS = ["ASC", "DESC"];

interface ServiceStoreOptions {
  apiVersion?: number;
  channels?: boolean;
  debugMode?: boolean;
  enableErrorNotifications?: boolean;
  disableCreatedListener?: boolean;
  disableUpdatedListener?: boolean;
  disablePatchedListener?: boolean;
  disableRemovedListener?: boolean;
  disableStatusListener?: boolean;
  enableEntityOptimization?: boolean;
  pagination?: number;
  enableStorage?: boolean;
  idField?: string;
}

interface ErrorHandlerError {
  name: string;
  message: string;
  hook?: {
    id?: number | string;
  };
  data?: {
    id?: number | string;
  };
  id?: number | string;
}

/**
 * Base interface for store items - used internally for compatibility
 * For typed access, use the generic type parameter
 */
interface ServiceStoreItem {
  id: number | string;
  [key: string]: unknown;
}

/**
 * Generic find response structure
 */
interface FindResponse<T = ServiceStoreItem> {
  data: T[];
  result?: { data: T[] };
  total: number;
}

/**
 * Generic create response - can be single entity or array
 */
type CreateResponse<T = ServiceStoreItem> = T | T[];

/**
 * Generic patch response - can be single entity, array, or wrapped in data
 */
type PatchResponse<T = ServiceStoreItem> =
  | T
  | T[]
  | { data: T[]; id?: never }
  | { id: string | number; data?: never };

/**
 * Generic update response
 */
type UpdateResponse<T = ServiceStoreItem> =
  | T
  | { data: T[] }
  | { id: string | number };

/**
 * Generic remove response
 */
type RemoveResponse<T = ServiceStoreItem> = T | T[] | { id: string | number };

interface ServiceError {
  name: string;
  message: string;
  hook?: {
    id?: number;
  };
}

/**
 * Generic ServiceStore with type-safe entity operations
 *
 * @template S - The service path string (e.g., 'customers', 'products')
 *
 * When S matches a key in ServiceTypeMap, full type inference is provided.
 * Otherwise, falls back to generic BaseEntity type for backward compatibility.
 *
 * @example
 * // Typed store - full IntelliSense for customer properties
 * const store = new ServiceStore<'customers'>(rootStore, 'customers', options);
 * const customers = store.entities; // Type: customer[]
 *
 * // Untyped store - fallback to BaseEntity
 * const store = new ServiceStore(rootStore, 'custom-service', options);
 * const items = store.entities; // Type: BaseEntity[]
 */
export default class ServiceStore<S extends string = string> {
  rootStore;
  debugMode: boolean = false;

  isServiceStore = true;

  service: FeathersService;
  storage: LocalForage | null = null;
  serviceRoute = "";
  firstLoad = false;

  name: S | undefined = undefined;
  idField: string = "id";
  jobNumberService: boolean = false;
  options: ServiceStoreOptions = {};
  apiVersion: number = 1;
  channelsEnabled: boolean = true;
  enableErrorNotifications: boolean = false;
  enableEntityOptimization: boolean = false;

  disableCreatedListener: boolean = false;
  disableUpdatedListener: boolean = false;
  disablePatchedListener: boolean = false;
  disableRemovedListener: boolean = false;
  disableStatusListener: boolean = false;

  // Internal items array - use `entities` getter for typed access
  items: ResolveEntity<S>[] = [];
  isBeingWatched = false;
  enableAutoChannel = false;

  errorOnGet: ServiceError | null = null;
  errorOnFind: ServiceError | null = null;
  errorOnCreate: ServiceError | null = null;
  errorOnPatch: ServiceError | null = null;
  errorOnUpdate: ServiceError | null = null;
  errorOnRemove: ServiceError | null = null;

  isGetPending = false;
  isFindPending = false;
  isCreatePending = false;
  isPatchPending = false;
  isUpdatePending = false;
  isRemovePending = false;

  pagination = {
    enabled: false,
    total: 0,
    currentPage: 0,
    pageSize: 250,
    query: {},
    sortKey: "",
    sortDirection: SORT_DIRECTION_OPTIONS[0],
  };

  pageDataSet: { pageNumber: number; pageItems: (string | number)[] }[] = [];

  firsLoadDispose: (() => void) | undefined;
  disposeEntityOptimization: (() => void) | undefined;
  reactionManager: ReactionManager | null = null;
  errorReactionsBatchDispose: (() => void) | undefined;

  /**
   * Creates a new ServiceStore instance
   *
   * @param rootStore - The root MobX store
   * @param name - The service path (e.g., 'customers', 'products')
   * @param options - Configuration options for the store
   */
  constructor(rootStore: RootStore, name: S, options: ServiceStoreOptions) {
    this.name = name;
    this.rootStore = rootStore;
    this.apiVersion = options?.apiVersion ? options.apiVersion : 1;
    this.serviceRoute =
      this.apiVersion > 1 ? `v${this.apiVersion}/${name}` : name;
    this.service = rootStore.app.service(this.serviceRoute);
    this.channelsEnabled =
      options.channels || rootStore.mode !== "test" || true;
    this.debugMode = options.debugMode || false;
    this.enableErrorNotifications = options.enableErrorNotifications || false;
    this.disableCreatedListener =
      rootStore.mode === "test" || options.disableCreatedListener || false;
    this.disableUpdatedListener =
      rootStore.mode === "test" || options.disableUpdatedListener || false;
    this.disablePatchedListener =
      rootStore.mode === "test" || options.disablePatchedListener || false;
    this.disableRemovedListener =
      rootStore.mode === "test" || options.disableRemovedListener || false;
    this.disableStatusListener =
      rootStore.mode === "test" || options.disableStatusListener || false;

    this.enableEntityOptimization = options.enableEntityOptimization || false;

    // Use makeObservable instead of makeAutoObservable to support subclassing
    // NOTE: CRUD methods (find, get, create, patch, update, remove) are NOT annotated here
    // because they are arrow function properties that may be overridden by subclasses
    // (e.g., OfflineServiceStore). Annotating them would make them non-configurable.
    makeObservable(this, {
      // Observables
      items: observable,
      isBeingWatched: observable,
      errorOnGet: observable,
      errorOnFind: observable,
      errorOnCreate: observable,
      errorOnPatch: observable,
      errorOnUpdate: observable,
      errorOnRemove: observable,
      isGetPending: observable,
      isFindPending: observable,
      isCreatePending: observable,
      isPatchPending: observable,
      isUpdatePending: observable,
      isRemovePending: observable,
      pagination: observable,
      pageDataSet: observable,
      firstLoad: observable,
      // Computed
      entities: computed,
      shouldRunFirstLoad: computed,
      // Actions - excluding CRUD methods that subclasses may override
      addOrUpdateEvent: action,
      removeEvent: action,
      mergeItems: action,
      replaceAllItems: action,
      clearAll: action,
      clearData: action,
      clearPageData: action,
      clearErrors: action,
      clearPending: action,
      enablePagination: action,
      setPageTotal: action,
      setPageQuery: action,
      setPageSortKey: action,
      setPageSortDirection: action,
      updatePage: action,
      setServiceIdField: action,
      setObservability: action,
      loadStorage: action,
    });

    if (options.idField) {
      // if service has socket
      this.setServiceIdField(options.idField);
    }
    if (this.channelsEnabled) this.handleEvents();
    if (options.pagination) this.enablePagination(options.pagination);
    if (options.enableStorage) {
      this.storage = localforage.createInstance({
        name: this.rootStore.appName,
        storeName: this.name,
      });

      this.firsLoadDispose = when(
        () => this.shouldRunFirstLoad,
        () => {
          return this.loadStorage();
        }
      );
    }

    if (this.enableEntityOptimization) {
      this.disposeEntityOptimization = reaction(
        () => rootStore.currentChannelServices,
        (channelReaction: string[]) => {
          const serviceInChannels = channelReaction.findIndex(
            (
              /** @type {string | string[]} */ channelRoute: string | string[]
            ) => channelRoute.includes(`${this.name}`)
          );
          const shouldWatch = serviceInChannels > -1;
          if (shouldWatch) {
            this.debugMode && console.warn("shouldWatch", this.name);
            return this.setObservability(true);
          }
          return this.setObservability(false);
        },
        { fireImmediately: true }
      );
    } else {
      this.handleFirstLoad();
    }

    // OPTIMIZED: Replace 5 individual error reactions with batched reaction
    this.reactionManager = new ReactionManager(this.debugMode);
    this.reactionManager.createReactionBatch(`${this.name}-errors`, {
      delay: 50, // Batch error handling with 50ms delay
      maxWait: 200, // Maximum wait before forcing execution
      trailing: true,
    });

    // Add all error reactions to the batch
    this.reactionManager.addToBatch(
      `${this.name}-errors`,
      "all-errors",
      () => ({
        create: this.errorOnCreate,
        patch: this.errorOnPatch,
        remove: this.errorOnRemove,
        find: this.errorOnFind,
        get: this.errorOnGet,
      }),
      (current, previous) => {
        // Handle any new errors in batch
        Object.entries(current).forEach(([operation, error]) => {
          const prevError = previous?.[operation as keyof typeof previous];
          if (error && error !== prevError) {
            this.errorHandler(error);
          }
        });
      },
      { fireImmediately: true }
    );

    this.reactionManager.activateBatch(`${this.name}-errors`);
    this.errorReactionsBatchDispose = () => {
      this.reactionManager?.disposeBatch(`${this.name}-errors`);
    };

    onBecomeObserved(this, "entities", () =>
      this.handleBecomeObserved("resume")
    );
    onBecomeUnobserved(this, "entities", () =>
      this.handleBecomeObserved("suspend")
    );
  }

  handleBecomeObserved = (/** @type {string} */ value: string) => {
    if (this.debugMode)
      console.log(
        `${this.name} - handleBecomeObserved entities - value ${value}`
      );
    if (value === "resume" && !this.enableAutoChannel) {
      runInAction(() => {
        this.enableAutoChannel = true;
      });
    }
    if (value === "suspend" && this.enableAutoChannel) {
      runInAction(() => {
        this.enableAutoChannel = false;
      });
    }
    return value;
  };

  errorHandler = (error: ErrorHandlerError): ErrorHandlerError => {
    const { name, message } = error;
    console.error("error on service: ", this.name);
    console.error("error", error);
    if (name === "NotAuthenticated") {
      if (this.rootStore.routerStore) {
        this.handleNotAuthenticated(message);
      }
    }
    if (name === "NotFound") {
      // Try to extract the ID from multiple possible locations
      let id: string | number | undefined =
        error?.hook?.id || error?.data?.id || error?.id;

      // If ID not found in error properties, try parsing from message
      // Message format: "No record found for id 'XXXXXXXX'"
      if (!id && message) {
        const idMatch = message.match(/id\s+['"]([^'"]+)['"]/i);
        if (idMatch && idMatch[1]) {
          id = idMatch[1];
        }
      }

      // Remove the entity from local storage if we found an ID
      if (id) {
        this.removeEvent({ id: id.toString() });
      }
    }

    if (this.enableErrorNotifications) {
      this.handleErrorNotification(name, message);
    }
    return error;
  };

  setObservability = (/** @type {boolean} */ value: boolean) => {
    this.debugMode &&
      console.warn(`Debug setObservability ${this.name}, ${value}`);

    if (value && !this.firstLoad) this.handleFirstLoad();
    this.isBeingWatched = value;
    return value;
  };

  handleErrorNotification = (
    /** @type {string} */ name: string,
    /** @type {string} */ message: string
  ) => {
    console.warn("handleErrorNotification", name, message);
    if (this.rootStore.notificationStore) {
      this.rootStore.notificationStore.addNotification({
        message: message,
        options: { variant: "warning" },
      });
    }
    return message;
  };

  handleFirstLoad = () => {
    if (!this.firstLoad && this.storage) {
      runInAction(() => {
        this.firstLoad = true;
      });
    }
    return;
  };

  handleNotAuthenticated = async (/** @type {string} */ message: string) => {
    if (!this.rootStore.routerStore || !this.rootStore.authStore) {
      console.warn("User was not authenticated", message);
      return;
    }
    const { handleAuthReroute, params, queryParams } =
      this.rootStore.routerStore;
    const route = this.rootStore.routerStore.route;
    const checkRefresh = await this.rootStore.authStore.routerStoreAuthCheck();
    if (!checkRefresh) {
      if (this.rootStore.notificationStore) {
        this.rootStore.notificationStore.addNotification({
          message: message,
          options: { variant: "warning" },
        });
      } else {
        console.warn("User was not authenticated", message);
      }
      if (route) {
        return handleAuthReroute(route, params, queryParams);
      }
    }
  };

  setServiceIdField = async (/** @type {string} */ idField: string) => {
    this.debugMode && console.warn("Debug setServiceIdField for", idField);
    if (idField !== this.idField) {
      runInAction(() => {
        this.idField = idField;
      });
    }
    // if (
    //   name === 'api-services' ||
    //   (this.rootStore.mode.includes('dev') && name.includes('azure/')) ||
    //   (!this.rootStore.mode.includes('prod') && name.includes('sage/'))
    // ) {
    //   return name;
    // }
    // if (this.rootStore.mode === 'test') {
    //   runInAction(() => {
    //     this.idField = 'id';
    //   });
    //   return name;
    // }
    // try {
    //   const { id, jobNumberService = false } = await this.rootStore.app
    //     .service('api-services')
    //     .get(name);
    //   this.debugMode && console.warn('Debug setServiceIdField found idField', id);
    //   runInAction(() => {
    //     if (id && id !== this.idField) {
    //       this.idField = id;
    //     }
    //     if (jobNumberService) {
    //       this.jobNumberService = jobNumberService;
    //     }
    //   });
    // } catch (err) {
    //   console.warn(`${name}: setServiceIdField Error`, err);
    // }
    return idField;
  };

  // handleStorageSync = async (
  //   /** @type {{ getItem: (arg0: string) => any; setItem: (arg0: string, arg1: string) => any; }} */ storage
  // ) => {
  //   this.debugMode && console.warn('Debug handleStorageSync', this.name);
  //   try {
  //     const loadedStoreItems = await storage.getItem(
  //       `${this.name}-store-items`
  //     );
  //     if (loadedStoreItems) this.loadStoreItems(JSON.parse(loadedStoreItems));
  //     this.syncStorageDispose = reaction(
  //       () => this.watchItems,
  //       async items => {
  //         this.debugMode &&
  //           console.warn('Debug handleStorageSync item count', items.length);
  //         await storage.setItem(
  //           `${this.name}-store-items`,
  //           JSON.stringify({ items: [...items] })
  //         );
  //       },
  //       { delay: 7500 }
  //     );
  //   } catch (err) {
  //     console.warn('handleStorageSync Error', err);
  //   }
  // };

  loadStorage = () => {
    if (!this.storage) {
      return;
    }
    const loadedItems: ServiceStoreItem[] = [];
    const storageHandler = (item: ServiceStoreItem) => {
      loadedItems.push(item);
    };
    this.storage
      .iterate((value: ServiceStoreItem) => {
        return storageHandler(value);
      })
      .then(() => {
        this.mergeItems(loadedItems);
        if (this.debugMode)
          console.warn("Storage Loaded For Store:", this.name);
      })
      .catch(function (err: unknown) {
        // This code runs if there were any errors
        console.warn(err);
      });
  };

  handleEvents = () => {
    if (!this.service || !this.service.on) {
      console.warn(
        `Service ${this.name} does not have .on method. Skipping event listeners setup.`
      );
      return;
    }

    const setupListeners = () => {
      // Get fresh service reference after socket is connected
      const service = this.rootStore.app.service(this.serviceRoute);

      service.on("created", (item: ServiceStoreItem) => {
        runInAction(() => {
          this.addOrUpdateEvent(item, true);
        });
      });
      service.on("patched", (item: ServiceStoreItem) => {
        runInAction(() => {
          this.addOrUpdateEvent(item, true);
        });
      });
      service.on("removed", (item: ServiceStoreItem) => {
        runInAction(() => {
          this.removeEvent(item);
        });
      });
    };

    // Check if socket is already connected
    // TODO: Investigate why channel events aren't being received even after socket connects
    const socket = this.rootStore.app.io;
    if (socket?.connected) {
      setupListeners();
    } else if (socket) {
      socket.once("connect", () => {
        setupListeners();
      });
    } else {
      // Fallback: set up listeners anyway (they may work with REST)
      setupListeners();
    }
    // // // // !this.disableUpdatedListener &&
    // const handleUpdated = (item: any) => {
    //   runInAction(() => {
    //     console.warn('Debug on updated', item);
    //     this.addOrUpdateEvent(item, true);
    //   });
    // };
    // this.rootStore.app
    //   .service(this.name)
    //   .on('updated', (item: any) => handleUpdated);
    // // // // !this.disablePatchedListener &&
    // // if (this.name === 'user') {
    // //   console.log('SETTING UP USER PATCH LISTENER');
    // //   console.log('this.service', this.service);
    // // }
    // const handlePatched = (item: any) => {
    //   runInAction(() => {
    //     console.warn('Debug on patched', item);
    //     this.addOrUpdateEvent(item, true);
    //   });
    // };
    // this.rootStore.app
    //   .service(this.name)
    //   .on('patched', async (item: any) => handlePatched);
    // // // // !this.disableRemovedListener &&
    // const handleRemoved = (item: any) => {
    //   runInAction(() => {
    //     console.warn('Debug on removed', item);
    //     this.removeEvent(item);
    //   });
    // };
    // this.rootStore.app
    //   .service(this.name)
    //   .on('removed', async (item: any) => handleRemoved);
    // !this.disableStatusListener &&
    //   this.service.on(
    //     'status',
    //     (
    //       /** @type {{ message: string; ids: string | any[]; }} */ status: {
    //         message: string;
    //         ids: string | any[];
    //       }
    //     ) => {
    //       console.warn('service on status', status);
    //       if (status && status.message) {
    //         if (status.message === 'remove') {
    //           runInAction(() => {
    //             const newArray = this.items
    //               .slice()
    //               .filter(
    //                 (arrayItem: { [key: string]: any }) =>
    //                   !status.ids.includes(arrayItem[this.idField])
    //               );
    //             this.replaceAllItems(newArray);
    //           });
    //           if (this.rootStore.notificationStore)
    //             this.rootStore.notificationStore.addNotification({
    //               message: `Removed ${status.ids.length} old record(s)`,
    //               options: { variant: 'info' },
    //             });
    //         } else {
    //           console.warn('service on status', status.message);
    //           if (this.rootStore.notificationStore)
    //             this.rootStore.notificationStore.addNotification({
    //               message: status.message,
    //               options: { variant: 'info' },
    //             });
    //         }
    //       }
    //     }
    //   );
  };

  dispose = () => {
    this.debugMode && console.log("DISPOSE CALLED FOR SERVICE:", this.name);

    // Clear all reaction disposers to prevent memory leaks
    try {
      this.firsLoadDispose && this.firsLoadDispose();
      this.disposeEntityOptimization && this.disposeEntityOptimization();

      // OPTIMIZED: Dispose batched error reactions
      this.errorReactionsBatchDispose && this.errorReactionsBatchDispose();
      if (this.reactionManager) {
        this.reactionManager.disposeAll();
        this.reactionManager = null;
      }
    } catch (error) {
      console.error(`Error disposing reactions for ${this.name}:`, error);
    }

    // Remove all event listeners properly to prevent memory leaks
    if (this.channelsEnabled && this.service) {
      try {
        this.service.removeAllListeners("created");
        this.service.removeAllListeners("updated");
        this.service.removeAllListeners("patched");
        this.service.removeAllListeners("removed");
        this.service.removeAllListeners("status");
      } catch (error) {
        console.error(
          `Error removing event listeners for ${this.name}:`,
          error
        );
      }
    }

    // Clear storage reference
    this.storage = null;

    // Clear all observable state to help with garbage collection
    runInAction(() => {
      this.items = [];
      this.pageDataSet = [];
      this.clearErrors();
      this.clearPending();
    });

    this.debugMode &&
      console.log(`ServiceStore ${this.name} disposed successfully`);
  };

  handleDebugMode = () => {
    this.debugMode = !this.debugMode;
  };

  // loadStoreItems = (/** @type {{ items: any; }} */ loadedStoreItems) => {
  //   this.debugMode && console.warn('Debug loadStoreItems', loadedStoreItems);
  //   this.items = [...loadedStoreItems.items];
  //   return this.items;
  // };

  enablePagination = (/** @type { number} */ pageSize: number) => {
    this.debugMode && console.warn("Debug enablePagination", pageSize);
    this.pagination.enabled = true;
    if (!isNaN(pageSize)) this.pagination.pageSize = pageSize;
  };

  setPageTotal = (/** @type {number} */ newTotal: number) => {
    this.debugMode && console.warn("Debug setPageTotal", newTotal);
    this.pagination.total = newTotal;
  };

  setPageQuery = (/** @type {object} */ query: object) => {
    this.debugMode && console.warn("Debug setPageQuery", query);
    this.pagination.query = query;
  };
  setPageSortKey = (/** @type {string} */ sortKey: string) => {
    this.debugMode && console.warn("Debug setPageSortKey", sortKey);
    this.pagination.sortKey = sortKey;
  };
  setPageSortDirection = (/** @type {string} */ sortDirection: string) => {
    if (!SORT_DIRECTION_OPTIONS.includes(sortDirection)) {
      console.error(
        "Requires one of [",
        SORT_DIRECTION_OPTIONS.join(", "),
        `] but was given: "${sortDirection}"`
      );
      return;
    }
    this.debugMode && console.warn("Debug setPageSortDirection", sortDirection);
    this.pagination.sortDirection = sortDirection;
  };

  updatePage = async (/** @type {number} */ newPage: number) => {
    this.debugMode && console.warn("Debug updatePage", newPage);
    if (!this.pagination.enabled)
      throw Error("Enable pagination to use this action");
    this.pagination.currentPage = newPage;
    return await this.find();
  };

  /**
   * @param {object[]} newItemArray
   * @description
   */
  mergeItems = (newItemArray: ServiceStoreItem[]) => {
    // console.log('newItemArray', newItemArray);
    this.debugMode &&
      console.warn("Debug mergeItems newItemArray", newItemArray);
    const newItemIds = newItemArray.map(
      (item: ServiceStoreItem) => item.id ?? item[this.idField]
    );
    // console.log('newItemIds', newItemIds);

    const entitiesNotIncludedInNewItemArray = this.entities
      .slice()
      .filter((item) => {
        const itemId =
          (item as ServiceStoreItem)[this.idField] ??
          (item as ServiceStoreItem).id;
        return !newItemIds.includes(itemId as string | number);
      });

    // console.log('entitiesNotIncludedInNewItemArray', entitiesNotIncludedInNewItemArray);

    const combinedItems = [
      ...entitiesNotIncludedInNewItemArray,
      ...newItemArray,
    ];
    // console.log('combinedItems', combinedItems);
    this.debugMode && console.log("combinedItems.length", combinedItems.length);
    this.replaceAllItems(combinedItems);
  };

  /**
   * @param {object[]} newItemArray
   * @description
   */
  replaceAllItems = (newItemArray: object[]) => {
    this.debugMode &&
      console.warn("Debug replaceAllItems newItemArray", newItemArray);
    if (!newItemArray) {
      throw new Error("Requires items array");
    }
    this.debugMode &&
      console.warn(
        "Debug replaceAllItems this.items.length before",
        this.items.length
      );
    // console.log('replaceAllItems newItemArray', newItemArray);
    // @ts-expect-error - mobx types are not correct
    this.items.replace(newItemArray);
    // runInAction(() => {
    //   // console.log('this.items before', this.items);
    //   this.items = [...newItemArray];
    //   // console.log('this.items after', this.items);
    // });

    this.debugMode &&
      console.warn(
        "Debug replaceAllItems this.items.length after",
        this.items.length
      );
  };

  /**
   * Add or update an item in the local store
   * @param item - Item to add or update
   * @param shouldSetStorage - Whether to persist to storage
   * @returns The item
   */
  addOrUpdateEvent = (
    item: Record<string, unknown>,
    shouldSetStorage: boolean = false
  ): Record<string, unknown> => {
    this.debugMode && console.warn("Debug addOrUpdateEvent item", item);

    const foundIndex = this.items.findIndex(
      (arrayItem) =>
        String((arrayItem as Record<string, unknown>)[this.idField]) ===
        String(item[this.idField])
    );

    if (foundIndex === -1) {
      // Cast to ResolveEntity for internal storage
      this.items.push(item as ResolveEntity<S>);
      if (shouldSetStorage && this.storage) {
        this.debugMode &&
          console.warn(
            "Debug addOrUpdateEvent storage.setItem",
            `${item.id}`,
            item
          );
        const safeItem = this.safeSerializeForStorage(item);
        this.storage
          .setItem(`${item.id}`, safeItem as ServiceStoreItem)
          .catch((error: unknown) => {
            this.handleStorageError(error, item);
          });
      }
    } else {
      // Update existing item
      const updated = Object.assign(
        this.items[foundIndex] as Record<string, unknown>,
        item
      );
      this.items[foundIndex] = updated as ResolveEntity<S>;

      if (shouldSetStorage && this.storage) {
        const storedItem = this.items[foundIndex] as Record<string, unknown>;
        this.debugMode &&
          console.warn(
            "Debug addOrUpdateEvent storage.setItem",
            `${storedItem.id}`,
            storedItem
          );
        const safeItem = this.safeSerializeForStorage(storedItem);
        this.storage
          .setItem(`${storedItem.id}`, safeItem as ServiceStoreItem)
          .catch((error: unknown) => {
            this.handleStorageError(error, storedItem);
          });
      }
    }

    return item;
  };

  /**
   * Remove an item from the local store
   * @param item - Item to remove (can be id, object with id, or full item)
   */
  removeEvent = async (
    item: { id?: string | number; [key: string]: unknown } | number | string
  ) => {
    this.debugMode && console.warn("Debug removeEvent", item);
    if (!item) {
      console.log("Needs something to remove");
      return;
    }
    if (this.items.length === 0) {
      console.log("Current device did not have any items to remove");
      return;
    }

    this.debugMode && console.warn("Debug removeEvent for items", item);
    // Extract ID based on type of item
    let id: string | number;
    if (typeof item === "number" || typeof item === "string") {
      id = item;
    } else if (item[this.idField] !== undefined) {
      id = item[this.idField] as string | number;
    } else if (item.id !== undefined) {
      id = item.id;
    } else {
      console.error("Cannot extract ID from item:", item);
      return;
    }

    if (this.storage) {
      const foundItem = (await this.storage
        .getItem(`${id}`)
        .catch((error: unknown) => {
          console.error(
            `Something went wrong trying to get an item from ${this.name}Store`,
            error
          );
        })) as ServiceStoreItem | null;
      if (foundItem)
        this.storage.removeItem(`${id}`).catch((error: unknown) => {
          console.error(
            `Something went wrong trying to remove an item from ${this.name}Store`,
            error
          );
        });
    }
    const newArray = this.items
      .slice()
      .filter(
        (arrayItem) =>
          String(id) !== String((arrayItem as ServiceStoreItem)[this.idField])
      );
    this.replaceAllItems(newArray as object[]);
  };

  clearAll = () => {
    this.debugMode && console.warn("Debug clearAll", this.name);
    this.clearData();
    this.clearErrors();
    this.clearPending();
    this.clearStorage();
  };

  clearData = () => {
    this.debugMode && console.warn("Debug clearData", this.name);
    this.items = [];
    if (this.pagination.enabled) {
      this.clearPageData();
    }
  };

  clearPageData = () => {
    this.debugMode && console.warn("Debug clearData", this.name);
    if (this.pagination.enabled) {
      this.pagination = {
        enabled: true,
        total: 0,
        currentPage: 0,
        pageSize: 250,
        query: {},
        sortKey: "",
        sortDirection: SORT_DIRECTION_OPTIONS[0],
      };
    }
  };

  clearStorage = async () => {
    this.debugMode &&
      console.warn("Debug clearStorage", `${this.name}-store-items`);
    if (this.storage) {
      await this.storage.clear().catch((error: unknown) => {
        console.error(
          `Something went wrong trying to remove all items from ${this.name}Store`,
          error
        );
      });
    }
  };

  clearErrors = () => {
    this.debugMode && console.warn("Debug clearErrors", this.name);
    this.errorOnGet = null;
    this.errorOnFind = null;
    this.errorOnCreate = null;
    this.errorOnPatch = null;
    this.errorOnRemove = null;
  };

  clearPending = () => {
    this.debugMode && console.warn("Debug clearPending", this.name);
    this.isGetPending = false;
    this.isFindPending = false;
    this.isCreatePending = false;
    this.isPatchPending = false;
    this.isRemovePending = false;
  };
  /**
   * Get a single entity by ID
   *
   * @param id - The entity ID
   * @param params - Optional query parameters
   * @returns The entity if found, undefined otherwise
   */
  get = async (
    id: number | string,
    params: { query?: Partial<ResolveQuery<S>> } = {}
  ): Promise<ResolveEntity<S> | undefined> => {
    this.debugMode && console.warn("Debug get", id, params);
    this.isGetPending = true;
    let response: ResolveEntity<S> | null = null;
    try {
      response = await this.service.get(id, params);
      runInAction(async () => {
        if (response) {
          this.addOrUpdateEvent(response as ServiceStoreItem, true);
        }
        this.isGetPending = false;
      });
    } catch (error: unknown) {
      runInAction(() => {
        this.errorOnGet = error as ServiceError;
        this.isGetPending = false;
      });
    }
    if (response) return response;
  };

  // /**
  //  * findOne
  //  * @param {object} params
  //  * @returns
  //  */
  // findOne = async (params: object = {}) => {
  //   this.isFindPending = true;
  //   let response: any;

  //   try {
  //     this.debugMode && console.warn('Debug findOne', params);
  //     response = await this.service.findOne(params);
  //     runInAction(async () => {
  //       if (response) {
  //         this.debugMode && console.warn('Debug findOne response', response);
  //         this.addOrUpdateEvent(response, true);
  //       } else {
  //         this.debugMode &&
  //           console.warn('Debug findOne response no result', response);
  //       }
  //       this.isFindPending = false;
  //     });
  //   } catch (error: unknown) {
  //     runInAction(() => {
  //       this.errorOnFind = error;
  //       this.isFindPending = false;
  //     });
  //   }
  //   if (response) return response;
  // };

  /**
   * Find entities matching the query
   *
   * @param params - Query parameters including filters, sorting, and pagination
   * @returns FindResponse with matching entities
   */
  find = async (
    params: {
      query?: Partial<ResolveQuery<S>> & { [key: string]: unknown };
    } = {}
  ): Promise<FindResponse<ResolveEntity<S>> | null> => {
    this.isFindPending = true;
    let response: FindResponse<ResolveEntity<S>> | null = null;

    try {
      if (this.pagination.enabled) {
        if (!params.query)
          Object.assign(params, {
            query: {},
          });

        if (
          params.query &&
          Object.keys(params.query).length &&
          Object.keys(this.pagination.query).length !==
            Object.keys(params.query).length
        ) {
          this.setPageQuery(params.query);
        }

        Object.assign(params.query || {}, {
          $limit: this.pagination.pageSize,
          $skip: this.pagination.pageSize * this.pagination.currentPage,
        });
        if (Object.keys(this.pagination.query).length) {
          Object.assign(params.query || {}, {
            ...this.pagination.query,
          });
        }
      }
      this.debugMode && console.warn("Debug find", params);
      response = await this.service.find(params);

      if (response && response.data) {
        // console.log(`Service: ${this.name} response.data:`, response.data);
        this.debugMode && console.warn("Debug find response data", response);
        if (response.data.length) {
          runInAction(() => {
            response.data.forEach((item) =>
              this.addOrUpdateEvent(item as Record<string, unknown>, true)
            );
          });
          runInAction(() => {
            this.isFindPending = false;
          });
          return response;
        }

        if (this.pagination.enabled) {
          runInAction(() => {
            if (response.total !== this.pagination.total)
              this.setPageTotal(response.total || response.data.length);
          });
          await this.setPageDataSet({
            pageNumber: this.pagination.currentPage,
            pageItems: response.data.map(
              (item) =>
                (item as Record<string, unknown>)[this.idField] as
                  | string
                  | number
            ),
          });
        }
      } else if (
        response &&
        response.result &&
        response.result.data &&
        response.result.data.length
      ) {
        this.debugMode && console.warn("Debug find response result", response);
        if (response.result.data.length) {
          runInAction(() => {
            response.result!.data.forEach((item) =>
              this.addOrUpdateEvent(item as Record<string, unknown>, true)
            );
          });
        }

        if (this.pagination.enabled) {
          runInAction(() => {
            if (response.total !== this.pagination.total)
              this.setPageTotal(response.total || response.result!.data.length);
          });
          await this.setPageDataSet({
            pageNumber: this.pagination.currentPage,
            pageItems: response.result!.data.map(
              (item) =>
                (item as Record<string, unknown>)[this.idField] as
                  | string
                  | number
            ),
          });
        }
      } else {
        this.debugMode &&
          console.warn("Debug find response no data or result", response);
      }

      runInAction(() => {
        this.isFindPending = false;
      });
    } catch (error: unknown) {
      runInAction(() => {
        this.errorOnFind = error as ServiceError;
        this.isFindPending = false;
      });
    }
    return response;
  };

  /**
   * Create a new entity
   *
   * @param data - The entity data to create
   * @param params - Optional query parameters
   * @returns The created entity
   */
  create = async (
    data: ResolveData<S>,
    params: object = {}
  ): Promise<CreateResponse<ResolveEntity<S>> | undefined> => {
    this.debugMode && console.warn("Debug create", data, params);
    this.isCreatePending = true;
    let response: CreateResponse<ResolveEntity<S>> | null = null;
    try {
      response = await this.service.create(data, params);
      runInAction(async () => {
        if (!this.channelsEnabled) {
          if (response) {
            this.debugMode &&
              console.warn("Debug create response data", response);
            // Handle both single item and array responses
            if (Array.isArray(response)) {
              response.forEach((item) =>
                this.addOrUpdateEvent(item as Record<string, unknown>, true)
              );
            } else {
              this.addOrUpdateEvent(response as Record<string, unknown>, true);
            }
          } else {
            this.debugMode &&
              console.warn("Debug create response no data or result", response);
          }
        }
        this.isCreatePending = false;
      });
    } catch (error: unknown) {
      runInAction(() => {
        this.errorOnCreate = error as ServiceError;
        this.isCreatePending = false;
      });
    }
    if (response) return response;
  };

  /**
   * Patch (partial update) an entity
   *
   * @param id - The entity ID (null for multi-patch with query)
   * @param data - The partial data to update
   * @param params - Optional query parameters
   * @returns The patched entity or array of entities
   */
  patch = async (
    id: number | string | null,
    data: Partial<ResolvePatch<S>>,
    params: object = {}
  ): Promise<PatchResponse<ResolveEntity<S>> | undefined> => {
    this.debugMode && console.warn("Debug patch", id, data, params);
    this.isPatchPending = true;
    let response: PatchResponse<ResolveEntity<S>> | null = null;
    try {
      response = await this.service.patch(id, data, params);
      if (!this.channelsEnabled && response) {
        runInAction(() => {
          // TODO: Investigate why channel events aren't being received - for now, always update locally
          // Channel events should still work for real-time updates from other users
          // Handle multi-patch response (array returned directly when using query)
          if (Array.isArray(response)) {
            this.debugMode &&
              console.warn(
                "Debug patch response array (multi-patch)",
                response
              );
            response.forEach((item) =>
              this.addOrUpdateEvent(item as Record<string, unknown>, true)
            );
          } else if (response) {
            const respObj = response as Record<string, unknown>;
            if (respObj.id !== undefined) {
              this.debugMode &&
                console.warn("Debug patch response data", response);
              this.addOrUpdateEvent(respObj, true);
            } else if (respObj.data && Array.isArray(respObj.data)) {
              this.debugMode &&
                console.warn("Debug patch response data array", response);
              (respObj.data as Record<string, unknown>[]).forEach((item) =>
                this.addOrUpdateEvent(item, true)
              );
            }
          }
          this.isPatchPending = false;
        });
      }
    } catch (error: unknown) {
      runInAction(() => {
        this.errorOnPatch = error as ServiceError;
        this.isPatchPending = false;
      });
    }
    if (response) return response;
  };

  /**
   * Update (full replace) an entity
   *
   * @param id - The entity ID
   * @param data - The complete entity data
   * @param params - Optional query parameters
   * @returns The updated entity
   */
  update = async (
    id: number | string,
    data: ResolveData<S>,
    params: object = {}
  ): Promise<UpdateResponse<ResolveEntity<S>> | undefined> => {
    this.debugMode && console.warn("Debug update", id, data);
    this.isUpdatePending = true;
    let response: UpdateResponse<ResolveEntity<S>> | null = null;
    try {
      response = await this.service.update(id, data, params);
      runInAction(async () => {
        if (!this.channelsEnabled && response) {
          const respObj = response as Record<string, unknown>;
          if (respObj.id !== undefined) {
            this.debugMode &&
              console.warn("Debug update response data", response);
            this.addOrUpdateEvent(respObj, true);
          } else if (respObj.data && Array.isArray(respObj.data)) {
            this.debugMode &&
              console.warn("Debug update response data array", response);
            (respObj.data as Record<string, unknown>[]).forEach((item) =>
              this.addOrUpdateEvent(item, true)
            );
          } else {
            this.debugMode &&
              console.warn("Debug update response no data or result", response);
          }
        }
        this.isUpdatePending = false;
      });
    } catch (error: unknown) {
      runInAction(() => {
        this.errorOnRemove = error as ServiceError;
        this.isUpdatePending = false;
      });
    }
    if (response) return response;
  };

  /**
   * Remove an entity
   *
   * @param id - The entity ID (null for multi-remove with query)
   * @param params - Optional query parameters
   * @returns The removed entity or array of entities
   */
  remove = async (
    id: number | string | null,
    params: object = {}
  ): Promise<RemoveResponse<ResolveEntity<S>> | undefined> => {
    this.debugMode && console.warn("Debug remove", id, params);
    let response: RemoveResponse<ResolveEntity<S>> | null = null;
    try {
      this.isRemovePending = true;
      response = await this.service.remove(id, params);
      runInAction(async () => {
        // Had to remove the channel check here to ensure local store updates
        if (response) {
          if (Array.isArray(response)) {
            response.forEach((i) =>
              this.removeEvent(i as Record<string, unknown>)
            );
          } else {
            const respObj = response as Record<string, unknown>;
            if (respObj.id !== undefined) {
              this.debugMode &&
                console.warn("Debug remove response data", response);
              this.removeEvent({ id: String(respObj.id) });
            }
          }
        }
        this.isRemovePending = false;
      });
    } catch (error: unknown) {
      runInAction(() => {
        this.errorOnRemove = error as ServiceError;
        this.isRemovePending = false;
      });
    }
    if (response) return response;
  };

  /**
   * Set page data set for pagination
   * @param item - Page number and item IDs
   */
  setPageDataSet = async (item: {
    pageNumber: number;
    pageItems: (string | number)[];
  }) => {
    const foundPageIndex = this.pageDataSet.findIndex(
      (i: { pageNumber: number }) => i.pageNumber === item.pageNumber
    );
    if (foundPageIndex > -1) {
      this.pageDataSet[foundPageIndex] = {
        pageNumber: item.pageNumber,
        pageItems: item.pageItems,
      };
    } else {
      this.pageDataSet.push({
        pageNumber: item.pageNumber,
        pageItems: item.pageItems,
      });
    }
  };

  /**
   * Get an entity by ID from the local store
   *
   * @param id - The entity ID
   * @returns The entity if found, undefined otherwise
   */
  getById(id: number | string): ResolveEntity<S> | undefined {
    return this.entities.find(
      (entity) =>
        String((entity as ServiceStoreItem)[this.idField]) === String(id)
    );
  }

  /**
   * Filter entities by a key-value pair
   *
   * @param key - The property key to filter by
   * @param value - The value to match
   * @returns Array of matching entities
   */
  filterByKeyValue(key: string, value: unknown): ResolveEntity<S>[] {
    return this.entities.filter(
      (entity) => (entity as ServiceStoreItem)[key] === value
    );
  }

  // get watchItems() {
  //   return this.items.slice().map(item => {
  //     return { ...item };
  //   });
  // }

  /**
   * Get all entities in the store
   * @returns Array of entities with full type inference
   */
  get entities(): ResolveEntity<S>[] {
    // console.log('entities this.items', this.items);
    if (this.enableEntityOptimization && !this.isBeingWatched)
      return [] as ResolveEntity<S>[];
    return this.items.slice().map((item) => {
      return { ...item } as ResolveEntity<S>;
    });
  }

  /**
   * Get entities for the current page (when pagination is enabled)
   * @returns Array of entities for the current page
   */
  get currentPageItems(): ResolveEntity<S>[] {
    if (!this.pagination.enabled) return [] as ResolveEntity<S>[];
    return this.entities
      .slice()
      .filter((item) => {
        const foundPageIndex = this.pageDataSet.findIndex(
          (i) => i.pageNumber === this.pagination.currentPage
        );
        if (foundPageIndex > -1) {
          const ids = this.pageDataSet[foundPageIndex].pageItems;
          const itemId = (item as ServiceStoreItem)[this.idField] as
            | string
            | number;
          return ids.includes(itemId);
        } else return false;
      })
      .sort((a, b) => {
        if (!this.pagination.sortKey) return 0;
        const aVal = (a as ServiceStoreItem)[this.pagination.sortKey] as number;
        const bVal = (b as ServiceStoreItem)[this.pagination.sortKey] as number;
        if (this.pagination.sortDirection === "ASC") return aVal - bVal;
        return bVal - aVal;
      })
      .map((item) => {
        return { ...item } as ResolveEntity<S>;
      });
  }
  get totalPages() {
    if (!this.pagination.enabled) return "N/A";
    return Math.ceil(this.pagination.total / this.pagination.pageSize - 0.001);
  }

  get isServicePending() {
    return (
      this.isGetPending ||
      this.isFindPending ||
      this.isCreatePending ||
      this.isPatchPending ||
      this.isRemovePending
    );
  }

  get serviceHasError() {
    return (
      !!this.errorOnGet ||
      !!this.errorOnFind ||
      !!this.errorOnCreate ||
      !!this.errorOnPatch ||
      !!this.errorOnRemove
    );
  }

  get shouldRunFirstLoad() {
    return this.firstLoad;
  }

  /**
   * @function safeSerializeForStorage
   * @param {object} item
   * @description Safely serialize an object for IndexedDB storage by removing non-serializable properties
   */
  private safeSerializeForStorage = (item: {
    [key: string]: unknown;
  }): object => {
    try {
      // Use JSON stringify/parse to remove non-serializable properties
      return JSON.parse(JSON.stringify(item));
    } catch (error) {
      // Fallback: create a clean object with only primitive values
      const safeItem: { [key: string]: unknown } = {};
      for (const [key, value] of Object.entries(item)) {
        if (value !== null && value !== undefined) {
          const valueType = typeof value;
          if (
            valueType === "string" ||
            valueType === "number" ||
            valueType === "boolean"
          ) {
            safeItem[key] = value;
          } else if (valueType === "object" && !Array.isArray(value)) {
            // Try to serialize nested objects safely
            try {
              safeItem[key] = JSON.parse(JSON.stringify(value));
            } catch {
              // Skip non-serializable nested objects
              continue;
            }
          } else if (Array.isArray(value)) {
            // Handle arrays by filtering serializable items
            try {
              safeItem[key] = JSON.parse(JSON.stringify(value));
            } catch {
              // Skip non-serializable arrays
              continue;
            }
          }
        }
      }
      return safeItem;
    }
  };

  /**
   * @function handleStorageError
   * @param {unknown} error
   * @param {object} item
   * @description Handle storage errors, particularly IndexedDB version conflicts
   */
  private handleStorageError = async (
    error: unknown,
    item: { [key: string]: unknown }
  ) => {
    if (error instanceof Error) {
      if (error.name === "VersionError" || error.message.includes("version")) {
        console.warn(
          `IndexedDB version conflict for ${this.name}Store, clearing cache and retrying...`
        );
        try {
          // Clear the problematic store and retry
          await this.storage?.clear();
          const safeItem = this.safeSerializeForStorage(item);
          const itemWithMetadata = {
            ...safeItem,
            _storeVersion: Date.now(),
            _lastUpdated: new Date().toISOString(),
          };
          await this.storage?.setItem(
            `${item.id}`,
            itemWithMetadata as unknown as ServiceStoreItem
          );
        } catch (retryError) {
          console.warn(
            `Failed to resolve IndexedDB version conflict for ${this.name}Store:`,
            retryError
          );
        }
      } else {
        console.error(
          `Something went wrong trying to add an item from ${this.name}Store`,
          error
        );
      }
    }
  };
}
